import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import {
  Button, Col, Modal, Row,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import validateForm from 'utils/Validator';
import Form from 'components/Form/Layout';
import { yupCreateDocRequest } from 'utils/yupFields';
import { LoadingContext } from 'context/Loading';
import logo from 'assets/img/logo-blue-full.png';
import TextField from 'components/Form/FormTextField';
import Select from 'components/Form/FormSelect';
import Datepicker from 'components/Form/FormDatepicker';
import { DocRequestTemplateCategoryType } from 'types/DocRequestTemplate';
import SuccessMessage from 'components/Modal/SuccessMessage';
import { DocRequestForm, DocRequestStatus } from 'types/DocRequest';
import { IoArrowForwardCircleOutline } from 'react-icons/io5';
import ProfileService from 'services/api/Profile';
import handlePromise from 'utils/Promise';
import { useParams } from 'react-router';
import { Profile } from 'types/User';
import DocsRequestApi from 'services/api/DocsRequest';
import { formatToDate } from 'utils/date';
import DocStatusBadge from '../StatusBadge';

type CreateRequestModalProps = {
  category: DocRequestTemplateCategoryType
  onClose: () => void,
  reloadData: () => void,
};

function CreateRequestModal( {
  category, onClose, reloadData,
}: CreateRequestModalProps ) {
  const { id } = useParams();
  const { t } = useTranslation();
  const { toggleLoading } = useContext( LoadingContext );
  const [formSubmitted, setFormSubmitted] = useState( false );
  const validate = validateForm( () => yupCreateDocRequest( category ) );
  const [dataLoading, setDataLoading] = useState<boolean>( false );
  const [contacts, setContacts] = useState( [] );

  const fetchData = useCallback( async () => {
    if ( id ) {
      setDataLoading( true );
      const [, response, responseData] = await handlePromise(
        ProfileService.getAllByCuentaDeInversionBackOffice(
          { cuentaDeInversionId: id },
        ),
      );
      setDataLoading( false );

      if ( response.ok ) {
        setContacts( responseData.data?.map( ( item: Profile ) => ( {
          id: item.contacId,
          name: `${item.firstName || ''} ${item.lastName || ''}`,
        } ) ) );
      }
    }
  }, [] );

  useEffect( () => {
    fetchData();
  }, [] );

  const submitForm = useCallback( async ( formValues: DocRequestForm ) => {
    toggleLoading( true );
    const dataToSend = {
      cuentaInversionId: id,
      solicitudData: {
        nombre: formValues.nombre,
        descripcion: formValues.descripcion,
        contactoID: formValues.contactoID,
        categoria: category,
        estado: 'PENDIENTE',
        activo: true,
        fechaLimite: formValues.fechaLimite
          ? formatToDate( formValues.fechaLimite ).toISOString() : undefined,
        fechaExpiracion: formValues.fechaExpiracion
          ? formatToDate( formValues.fechaExpiracion ).toISOString() : undefined,
      },
    };

    const [, response] = await handlePromise(
      DocsRequestApi.createDocRequest( dataToSend ),
    );

    if ( response.ok ) {
      setFormSubmitted( true );
      reloadData();
    }
    toggleLoading( false );
    // ToDo show error
  }, [category, id] );

  return (
    <Modal
      show
      centered
      size="lg"
      onHide={onClose}
      className="table-docs"
    >
      <Modal.Header closeButton className="pb-0" />
      <Modal.Body className="pt-0">
        <div className="text-center">
          <img src={logo} alt="Grupo Impar" height={40} />
        </div>
        <Row className="mt-4">
          <Col md={11} className="m-auto">
            {formSubmitted
              ? (
                <SuccessMessage
                  description={t( 'admin.docs.createSuccess' )}
                  onClose={onClose}
                />
              )
              : (
                <Form
                  validate={validate}
                  onSubmit={( values ) => submitForm( values as DocRequestForm )}
                  body={(
                    <>
                      <h2 className="text-primary">{ t( 'admin.docs.create' )}</h2>
                      <Row className="my-3 align-items-center">
                        <Col xs="auto">
                          <h3 className="text-primary mb-0">{ t( 'admin.docs.field.status' )}</h3>
                        </Col>
                        <Col xs="auto">
                          <DocStatusBadge status={DocRequestStatus.pending} />
                        </Col>
                      </Row>

                      <h3 className="text-primary mb-4">{ t( 'admin.docs.basicInfo' )}</h3>

                      <TextField
                        field="nombre"
                        label="admin.docs.field.title"
                      />
                      <TextField
                        field="descripcion"
                        label="admin.docs.field.description"
                      />
                      {category === DocRequestTemplateCategoryType.physical && (
                        <Select
                          field="contactoID"
                          label="admin.docs.field.contactId"
                          options={contacts}
                          isLoading={dataLoading}
                        />
                      )}
                      <Datepicker
                        field="fechaLimite"
                        label="admin.docs.field.limitDate"
                      />
                      <Datepicker
                        field="fechaExpiracion"
                        label="admin.docs.field.expirationDate"
                      />

                      <Row className="justify-content-end">
                        <Col xs="auto">
                          <Button
                            variant="primary"
                            type="submit"
                            size="sm"
                            className="btn--rounded text--normal text--xmd"
                          >
                            {t( 'admin.common.create' )}
                            <IoArrowForwardCircleOutline size={20} className="ms-2" />
                          </Button>
                        </Col>
                      </Row>
                    </>
                  )}
                />
              )}
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default CreateRequestModal;
